import React, { useState, useContext, useEffect, useRef } from "react";
import "./App.css";
import { getMonth } from "./util";
import CalendarHeader from "./components/CalendarHeader";
import Sidebar from "./components/Sidebar";
import Month from "./components/Month";
import Week from "./components/Week";
import DayView from "./components/DayView";
import GlobalContext from "./context/GlobalContext";
import EventModal from "./components/EventModal";
import ContextMenu from "./components/ContextMenu";
import LoginButton from "./components/LoginButton";
import LogoutPage from "./components/LogoutPage";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import LoginInfo from "./components/LoginInfo";
import BeheerPage from "./components/BeheerPage";
import UserListPage from "./components/UserListPage";
import UserDetailPage from "./components/UserDetailPage";
import CompanyListPage from "./components/CompanyListPage";
import CompanyDetailPage from "./components/CompanyDetailPage";
import { AuthContext } from 'react-oauth2-code-pkce'
import dayjs from "dayjs";

function App() {
  const [calendarView, setCalendarView] = useState("month");
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [currenMonth, setCurrentMonth] = useState(getMonth(currentDate.month(), currentDate.year()));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [swipeTransform, setSwipeTransform] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const { monthIndex, setMonthIndex, showEventModal, loadError } = useContext(GlobalContext);
  const { tokenData, token, logIn, logOut, idToken, error } = useContext(AuthContext);
  
  // Touch/swipe handling
  const touchStartX = useRef(0);
  const touchCurrentX = useRef(0);
  const isSwiping = useRef(false);

  // Auto-close sidebar on mobile when view changes
  useEffect(() => {
    if (window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  }, [calendarView, monthIndex]);

  // Update month grid when currentDate or view changes
  useEffect(() => {
    if (calendarView === "month") {
      setCurrentMonth(getMonth(currentDate.month(), currentDate.year()));
    }
  }, [currentDate, calendarView]);

  // Sync currentDate with monthIndex changes (from small calendar)
  useEffect(() => {
    if (typeof window !== 'undefined' && window.currentDate) {
      setCurrentDate(window.currentDate);
    }
  }, [monthIndex]);

  if (error) {
    return (
      <>
        <div style={{ color: 'red' }}>An error occurred during authentication: {error}</div>
        <a href="/login"><button>Log in again</button></a>
      </>
    )
  }

  // Navigation handlers
  function handlePrev() {
    if (calendarView === "month") {
      const newDate = currentDate.subtract(1, 'month');
      setCurrentDate(newDate);
      if (typeof window !== 'undefined') window.currentDate = newDate;
      if (typeof setMonthIndex === 'function') setMonthIndex(newDate.month());
    } else if (calendarView === "week") {
      setCurrentDate(currentDate.subtract(1, 'week'));
    } else if (calendarView === "day") {
      setCurrentDate(currentDate.subtract(1, 'day'));
    }
  }
  function handleNext() {
    if (calendarView === "month") {
      const newDate = currentDate.add(1, 'month');
      setCurrentDate(newDate);
      if (typeof window !== 'undefined') window.currentDate = newDate;
      if (typeof setMonthIndex === 'function') setMonthIndex(newDate.month());
    } else if (calendarView === "week") {
      setCurrentDate(currentDate.add(1, 'week'));
    } else if (calendarView === "day") {
      setCurrentDate(currentDate.add(1, 'day'));
    }
  }
  function handleToday() {
    setCurrentDate(dayjs());
    if (typeof window !== 'undefined') window.currentDate = dayjs();
    if (typeof setMonthIndex === 'function') setMonthIndex(dayjs().month());
  }

  // Swipe handlers for mobile navigation
  const handleTouchStart = (e) => {
    // Only enable swipe on mobile (not on desktop)
    if (window.innerWidth >= 768) return;
    // Don't interfere if sidebar is open or modal is showing
    if (sidebarOpen || showEventModal) return;
    
    touchStartX.current = e.touches[0].clientX;
    touchCurrentX.current = e.touches[0].clientX;
    isSwiping.current = true;
  };

  const handleTouchMove = (e) => {
    if (!isSwiping.current || window.innerWidth >= 768) return;
    if (sidebarOpen || showEventModal) return;
    
    touchCurrentX.current = e.touches[0].clientX;
    const diff = touchCurrentX.current - touchStartX.current;
    
    // Apply resistance at edges (limit swipe distance)
    const maxSwipe = 100;
    const resistance = 0.5;
    let transform = diff;
    
    if (Math.abs(diff) > maxSwipe) {
      transform = maxSwipe * Math.sign(diff) + (diff - maxSwipe * Math.sign(diff)) * resistance;
    }
    
    setSwipeTransform(transform);
  };

  const handleTouchEnd = () => {
    if (!isSwiping.current || window.innerWidth >= 768) return;
    if (sidebarOpen || showEventModal) return;
    
    const diff = touchCurrentX.current - touchStartX.current;
    const threshold = 50; // Minimum swipe distance to trigger navigation
    
    if (Math.abs(diff) > threshold) {
      setIsTransitioning(true);
      
      // Swipe right (diff > 0) = pulling content right = revealing PREVIOUS (left side)
      // Swipe left (diff < 0) = pulling content left = revealing NEXT (right side)
      const direction = diff > 0 ? 'right' : 'left';
      setSwipeTransform(direction === 'right' ? window.innerWidth : -window.innerWidth);
      
      setTimeout(() => {
        // First, slide current content off screen
        // (this part is already done by the setSwipeTransform above)
        
        // Update the data (new month/week/day)
        if (direction === 'right') {
          handlePrev(); // Swipe right = previous
        } else {
          handleNext(); // Swipe left = next
        }
      }, 200);
      
      // After navigation completes and new content is rendered
      setTimeout(() => {
        // Disable transition temporarily for instant positioning
        setIsTransitioning(false);
        
        // Position new content off-screen on the opposite side
        // When swiping left, position at RIGHT; when swiping right, position at LEFT
        const startPosition = direction === 'left' ? window.innerWidth : -window.innerWidth;
        setSwipeTransform(startPosition);
        
        // Wait for DOM to update with no-transition positioning
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Now enable transition and animate to center
            setIsTransitioning(true);
            setSwipeTransform(0);
            
            // Clean up after animation completes
            setTimeout(() => {
              setIsTransitioning(false);
            }, 200);
          });
        });
      }, 210);
    } else {
      // Snap back to original position
      setSwipeTransform(0);
    }
    
    isSwiping.current = false;
    touchStartX.current = 0;
    touchCurrentX.current = 0;
  };

  return (
    <Router>
      <Routes>
        <Route path="/logout" element={<LogoutPage />} />
        <Route path="/beheer" element={<BeheerPage />} />
        <Route path="/gebruikers" element={<UserListPage />} />
        <Route path="/gebruikers/:userId" element={<UserDetailPage />} />
        <Route path="/bedrijven" element={<CompanyListPage />} />
        <Route path="/bedrijven/:companyId" element={<CompanyDetailPage />} />
        <Route path="/logininfo" element={<LoginInfo />} />
        <Route path="/" element={
          <React.Fragment>
            {showEventModal && <EventModal />}
            <ContextMenu />
            <div className="h-screen flex flex-col overflow-hidden bg-white dark:bg-gray-950 text-gray-800 dark:text-gray-100">
              {/* Error banner if event loading fails */}
              {loadError && (
                <div style={{ background: '#fee', color: '#b00', padding: '12px', textAlign: 'center', fontWeight: 'bold', borderBottom: '2px solid #b00', zIndex: 50 }}>
                  {loadError}
                </div>
              )}
              <CalendarHeader
                calendarView={calendarView}
                setCalendarView={setCalendarView}
                currentDate={currentDate}
                onPrev={handlePrev}
                onNext={handleNext}
                onToday={handleToday}
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
              />
              <div className="flex flex-1 overflow-hidden relative">
                {/* Mobile sidebar overlay */}
                {sidebarOpen && (
                  <div 
                    className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
                    onClick={() => setSidebarOpen(false)}
                  />
                )}
                
                {/* Sidebar */}
                <div className={`
                  fixed md:relative 
                  top-0 left-0 
                  h-full 
                  w-full md:w-64 
                  bg-white dark:bg-gray-900
                  z-50 
                  transform transition-transform duration-300 ease-in-out
                  md:transform-none
                  ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
                  overflow-y-auto
                  border-r border-gray-100 dark:border-gray-800
                `}>
                  <Sidebar onClose={() => setSidebarOpen(false)} />
                </div>
                
                {/* Calendar content */}
                <div 
                  className="flex-1 overflow-auto"
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  style={{
                    transform: `translateX(${swipeTransform}px)`,
                    transition: isTransitioning ? 'transform 0.2s ease-out' : isSwiping.current ? 'none' : 'transform 0.3s ease-out',
                    touchAction: 'pan-y', // Allow vertical scrolling but control horizontal
                  }}
                >
                  {calendarView === "month" && <Month month={currenMonth} />}
                  {calendarView === "week" && <Week currentDate={currentDate} />}
                  {calendarView === "day" && <DayView currentDate={currentDate} />}
                </div>
              </div>
            </div>
          </React.Fragment>
        } />
      </Routes>
    </Router>
  );
}

export default App;
